/**
 * Pipeline for synchronous mapping/filtering/etc of iterable sequences. If you need asynchronous operations, use
 * RepeaterPipeline instead.
 */
export class Pipeline<T> implements Iterable<T> {
    constructor(readonly inner: Iterable<T>) {}

    static from<T>(inner: Iterable<T> | (() => Generator<T>)): Pipeline<T> {
        return new Pipeline<T>(typeof inner === 'function' ? inner() : inner);
    }

    static fromSingle<T>(value: T): Pipeline<T> {
        return new Pipeline(
            (function* (): Generator<T> {
                yield value;
            })(),
        );
    }

    static fromObjectValues(obj: Record<string, any>): Pipeline<unknown> {
        return new Pipeline(
            (function* (): Generator {
                for (const property in obj) {
                    yield obj[property];
                }
            })(),
        );
    }

    static empty<T>(): Pipeline<T> {
        return new Pipeline<T>((function* (): Generator<T> {})());
    }

    [Symbol.iterator](): Iterator<T> {
        return this.inner[Symbol.iterator]();
    }

    map<TMap>(mapper: (val: T, index: number) => TMap): Pipeline<TMap> {
        return this.wrapGenerator(function* (inner) {
            let index = 0;
            for (const item of inner) {
                yield mapper(item, index);
                index++;
            }
        });
    }

    forEach(action: (val: T) => void): void {
        for (const item of this.inner) {
            action(item);
        }
    }

    onEach(action: (val: T) => void): Pipeline<T> {
        return this.wrapGenerator(function* (inner) {
            for (const item of inner) {
                action(item);
                yield item;
            }
        });
    }

    flatMap<TMap>(mapper: (val: T, index: number) => Iterable<TMap>): Pipeline<TMap> {
        return this.wrapGenerator(function* (inner) {
            let index = 0;
            for (const parentItem of inner) {
                for (const childItem of mapper(parentItem, index)) {
                    yield childItem;
                }
                index++;
            }
        });
    }

    sum(mapper: (val: T) => number): number {
        let result = 0;
        for (const item of this.inner) {
            result += mapper(item);
        }
        return result;
    }

    /**
     * Iterates through the sequence to generate a dictionary/map.
     *
     * @param getKey - gets the value to use as the key for the resulting collection
     * @param getValue - gets the value to use as the value in the resulting collection (defaults to the whole/incoming
     *         value)
     * @param throwOnDuplicateKey - If true, throws an error if it encounters values with the same key. (This is
     *         not enforced by default.)
     */
    toMap<TKey, TVal = T>(
        getKey: (val: T) => TKey,
        getValue: (val: T) => TVal = v => v as unknown as TVal,
        throwOnDuplicateKey = false,
    ): Map<TKey, TVal> {
        const result = new Map<TKey, TVal>();

        for (const item of this.inner) {
            const key = getKey(item);

            if (throwOnDuplicateKey && result.has(key)) {
                throw new Error(`Pipeline.toMap() - Duplicate key "${key}"`);
            }

            result.set(key, getValue(item));
        }

        return result;
    }

    toGroupedMap<TKey, TVal = T>(
        getKey: (val: T) => TKey,
        getValue: (val: T) => TVal = v => v as unknown as TVal,
    ): Map<TKey, TVal[]> {
        const result = new Map<TKey, TVal[]>();

        for (const item of this.inner) {
            const k = getKey(item);

            let bucket = result.get(k);
            if (!bucket) {
                bucket = [];
                result.set(k, bucket);
            }
            bucket.push(getValue(item));
        }

        return result;
    }

    groupBy<TKey>(getKey: (val: T) => TKey): Pipeline<[TKey, T[]]> {
        return Pipeline.from(this.toGroupedMap(getKey));
    }

    filter(predicate: (val: T) => boolean): Pipeline<T> {
        return this.wrapGenerator(function* (inner) {
            for (const item of inner) {
                if (predicate(item)) yield item;
            }
        });
    }

    find(predicate: (val: T) => boolean): T | undefined {
        for (const item of this.inner) {
            if (predicate(item)) return item;
        }
        return undefined;
    }

    first(): T | undefined {
        // noinspection LoopStatementThatDoesntLoopJS
        for (const item of this.inner) {
            return item;
        }
        return undefined;
    }

    takeWhile(predicate: (val: T) => boolean): Pipeline<T> {
        return this.wrapGenerator(function* (inner) {
            for (const item of inner) {
                if (!predicate(item)) break;
                yield item;
            }
        });
    }

    skipUntil(predicate: (val: T) => boolean): Pipeline<T> {
        return this.wrapGenerator(function* (inner) {
            let foundStart = false;
            for (const item of inner) {
                if (!foundStart) {
                    if (predicate(item)) {
                        foundStart = true;
                    } else {
                        continue;
                    }
                }

                yield item;
            }
        });
    }

    reversed(): Pipeline<T> {
        return this.wrapGenerator(function* (inner) {
            const items = [...inner];
            for (let i = items.length - 1; i >= 0; i--) {
                yield items[i];
            }
        });
    }

    filterNarrow<TNarrow extends T>(predicate: (val: T) => val is TNarrow): Pipeline<TNarrow> {
        return this.filter(predicate) as Pipeline<TNarrow>;
    }

    chunked(chunkSize: number): Pipeline<T[]> {
        return this.wrapGenerator(function* (inner) {
            let buffer: T[] = [];
            for (const item of inner) {
                buffer.push(item);

                if (buffer.length >= chunkSize) {
                    yield buffer;
                    buffer = [];
                }
            }
            if (buffer.length > 0) yield buffer;
        });
    }

    count(): number {
        let result = 0;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        for (const _unused of this.inner) {
            result++;
        }
        return result;
    }

    some(predicate: (val: T) => boolean): boolean {
        for (const item of this.inner) {
            if (predicate(item)) return true;
        }
        return false;
    }

    every(predicate: (val: T) => boolean): boolean {
        for (const item of this.inner) {
            if (!predicate(item)) return false;
        }
        return true;
    }

    includes(val: T): boolean {
        return this.some(v => v === val);
    }

    concat(...sequences: Array<Iterable<T>>): Pipeline<T> {
        return this.wrapGenerator(function* (inner) {
            for (const item of inner) {
                yield item;
            }

            for (const seq of sequences) {
                for (const item of seq) {
                    yield item;
                }
            }
        });
    }

    /**
     * Apply an arbitrary transformation to this {@link Pipeline}.
     *
     * @param operator - a callback function that takes in the current pipeline and returns a wrapped version of
     *                   the pipeline
     */
    transform<TOut>(operator: (input: Pipeline<T>) => Pipeline<TOut>): Pipeline<TOut> {
        return operator(this);
    }

    concatIf(condition: boolean, seqGenerator: () => Iterable<T>): Pipeline<T> {
        return condition ? this.concat(seqGenerator()) : this;
    }

    toSet(): Set<T> {
        return new Set(this.inner);
    }

    toArray(): Array<T> {
        return [...this.inner];
    }

    uniq(): Pipeline<T> {
        const set = this.toSet();
        return new Pipeline<T>(set);
    }

    uniqBy(getKey: (val: T) => unknown): Pipeline<T> {
        const map = new Map<unknown, T>();

        for (const item of this.inner) {
            const key = getKey(item);
            if (!map.has(key)) {
                map.set(key, item);
            }
        }

        return Pipeline.from(map.values());
    }

    // wrapper to work around the "function" keyword not preserving the value of "this" like arrow functions do
    private wrapGenerator<TOut>(func: (inner: Iterable<T>) => Generator<TOut>): Pipeline<TOut> {
        return new Pipeline<TOut>(func(this.inner));
    }
}
